import { orpc } from 'src/api/rpc/client';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Refresh every task-list surface after a task mutation. Tasks render in
 * several places that query under different keys — the oRPC key factory
 * (e.g. the AR summary dashboard card) and the inbox's bespoke keys (task
 * table, detail pane, unified feed; see `use-inbox-data.ts`). Invalidating
 * all of them keeps the surfaces in sync no matter where the mutation
 * happened. Prefix matching catches all filtered/paged variants.
 */
export function invalidateTaskQueries(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['task-outcome-contract'] });
  void queryClient.invalidateQueries({ queryKey: orpc.tasks.list.key() });
  void queryClient.invalidateQueries({
    queryKey: orpc.tasks.agentRetry.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.tasks.listAssigneeFacets.key(),
  });
  // An agent page's running / queued / needs-input tile.
  void queryClient.invalidateQueries({
    queryKey: orpc.tasks.agentOpenCounts.key(),
  });
  void queryClient.invalidateQueries({ queryKey: ['task-list'] });
  void queryClient.invalidateQueries({ queryKey: ['task-detail'] });
  void queryClient.invalidateQueries({ queryKey: ['inbox-tasks'] });
  // The AR Tasks page lists billing tasks through its own contract, so a
  // complete/reassign from the task view must refresh its table and metrics.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arTasks.key(),
  });
  // The collection-thread reminder banner reads its task through the AR thread
  // contract, so a complete/cancel anywhere must clear it there too.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCollectionThreads.getReminderTask.key(),
  });
  // A cash receipt's lifecycle status is derived from its logging task, so
  // closing that task (e.g. as keyed on the task page) must refresh the
  // receipt list, detail, and work item.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.get.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.getWorkItem.key(),
  });
  // ...and its activity feed, which records the task's lifecycle events.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.listActivity.key(),
  });
  // Full-page task surfaces (queue page + detail page subtree).
  void queryClient.invalidateQueries({ queryKey: ['task-queue'] });
  void queryClient.invalidateQueries({ queryKey: ['task-subtasks'] });
  // Sidebar task-page count badges.
  void queryClient.invalidateQueries({
    queryKey: orpc.taskPages.counts.key(),
  });
  // Metric cards above a task page tab (counts, sums, aging).
  void queryClient.invalidateQueries({
    queryKey: orpc.taskTypes.metrics.key(),
  });
}
